// Implements: F-IDN-010, F-IDN-012, F-IDN-014, F-IDN-015, F-IDN-017
/**
 * `/cuenta/:tab`, the own account (mockup v3 P1, `vAccount`): one page header, "Cuenta", the six
 * section links and the section the address names. An unknown section lands on Perfil, and
 * `/sesiones` (the old page) redirects to the sessions section (router).
 *
 * The page only composes; every section owns its query in its container. The section is keyed by
 * the signed-in person's id (checklist 14), so no draft or step survives a change of account.
 * Sesiones reuses the `auth` list and gives it the account's toolbar ("Cerrar las demás
 * sesiones") and footer (the DB-IP credit): the page is the one place allowed to join two modules.
 */

import type { ReactElement } from "react";
import { Navigate, useParams } from "react-router";

import { AccountTabs, accountSectionHref, accountSectionOf } from "../../modules/account/components/AccountTabs";
import type { AccountSection } from "../../modules/account/components/AccountTabs";
import { SessionsFooter } from "../../modules/account/components/SessionsFooter";
import { AccessLogContainer } from "../../modules/account/containers/AccessLogContainer";
import { PasswordContainer } from "../../modules/account/containers/PasswordContainer";
import { PersonalDataContainer } from "../../modules/account/containers/PersonalDataContainer";
import { ProfileContainer } from "../../modules/account/containers/ProfileContainer";
import { SessionsToolbarContainer } from "../../modules/account/containers/SessionsToolbarContainer";
import { TotpContainer } from "../../modules/account/containers/TotpContainer";
import { SessionListContainer } from "../../modules/auth/containers/SessionListContainer";
import { useSession } from "../../shared/api/session";
import { useTranslation } from "../../shared/i18n";
import { PageHeader } from "../../shared/ui/molecules/PageHeader";

function SectionBody({ section }: { readonly section: AccountSection }): ReactElement {
  switch (section) {
    case "perfil":
      return <ProfileContainer />;
    case "contrasena":
      return <PasswordContainer />;
    case "2fa":
      return <TotpContainer />;
    case "sesiones":
      return (
        <SessionListContainer
          toolbar={(sessions) => <SessionsToolbarContainer sessions={sessions} />}
          footer={(sessions) => <SessionsFooter others={sessions.filter((session) => !session.current).length} />}
        />
      );
    case "accesos":
      return <AccessLogContainer />;
    case "datos":
      return <PersonalDataContainer />;
  }
}

export function AccountPage(): ReactElement {
  const { t } = useTranslation();
  const { tab = "" } = useParams();
  const { state } = useSession();
  const personId = state.status === "authenticated" ? state.view.user.id : "nobody";
  const section = accountSectionOf(tab);

  if (section === undefined) return <Navigate to={accountSectionHref("perfil")} replace />;
  return (
    <section>
      <PageHeader title={t("account.page.title")} subtitle={t("account.page.subtitle")} />
      <AccountTabs current={section} />
      <SectionBody key={personId} section={section} />
    </section>
  );
}
